import React, { useEffect } from 'react';
import { useStoreContext } from '../../../../App/StoreProvider';
import * as PumpScheduleEditActions from '../../../../../store/actions/pumpScheduleEdit';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { GET_LOFA_SCHEDULE, COUNT_LOADS_BY_JOB_WELLHEAD } from '../../../../../graphql/client/queries';
import {
    CountLoadsByJobWellhead,
    GetFullJobProfile_getJobProfile_pumpScheduleSummary as PumpScheduleSummaryData,
    GetLofaSchedule,
    GetLofaSchedule_getLofaSchedule as LOFAScheduleSummaryData,
} from '../../../../../types/schemaTypes';
import { EditActions } from '../../../../../components/_common/EditLoadErrors/EditLoadErrors';
import { Textfield } from '../../../../_common/Textfield/Textfield';
import { InputTitleEnum } from '../../../../_common/InputTitleEnum/InputTitleEnum';
import { THREE_NUMBERS_MASK } from '../../../../../utils/helpers/maskHelper';

interface WellheadRowProps {
    wellhead: PumpScheduleSummaryData;
    isDeleteEnabled: boolean;
    isNewRow: boolean;
    jobId: number;
}

const WellheadRow = ({ wellhead, isDeleteEnabled, isNewRow, jobId }: WellheadRowProps): React.ReactElement => {
    let count = 0;
    const lofaScheduleSummaryList: LOFAScheduleSummaryData[] = [];
    const proppantIds = wellhead.proppants && wellhead.proppants.map(it => it && it.proppantId);
    const [state, dispatch] = useStoreContext();
    const { proppantLoadsCountError, wellheadError, stagesError } = state.pumpScheduleEdit.fieldErrors[wellhead.id] || {
        proppantLoadsCountError: '',
        wellheadError: '',
        stagesError: '',
    };

    const { data: lofaData } = useQuery<GetLofaSchedule>(GET_LOFA_SCHEDULE, { variables: { jobId: jobId } });

    const [
        getLoadsOfJobWellname,
        { data: countLoadsData, loading: countLoadsLoading },
    ] = useLazyQuery<CountLoadsByJobWellhead>(COUNT_LOADS_BY_JOB_WELLHEAD, { fetchPolicy: 'network-only' });

    useEffect(() => {
        if (countLoadsData && countLoadsData.countLoadsByJobWellhead) {
            if (countLoadsData.countLoadsByJobWellhead.count > 0) {
                dispatch(
                    PumpScheduleEditActions.setFieldError(
                        'proppantLoadsCountError',
                        EditActions.WELLHEAD_PROPPANT_LOADS_COUNT_ERROR_MESSAGE,
                        wellhead.id,
                        null,
                    ),
                );
            } else {
                dispatch(PumpScheduleEditActions.deleteWellhead(wellhead));
            }
        }
    }, [countLoadsData, countLoadsLoading]);

    if (lofaData && lofaData.getLofaSchedule && lofaData.getLofaSchedule.length !== 0) {
        lofaData.getLofaSchedule.forEach((element: LOFAScheduleSummaryData | null): void => {
            if (element !== null) {
                lofaScheduleSummaryList.push(element);
            }
        });
    }

    proppantIds &&
        proppantIds.forEach(proppantId => {
            if (lofaScheduleSummaryList && lofaScheduleSummaryList.length > 0 && proppantId) {
                const jobsummaries =
                    lofaScheduleSummaryList &&
                    lofaScheduleSummaryList
                        .filter(it => (it && it.proppantId === proppantId) || 0)
                        .map(
                            it => it && it.jobSummaries && it.jobSummaries.filter(its => its && its.lofaName !== ''),
                        )[0];
                if (jobsummaries && jobsummaries.length > 0) {
                    count += 1;
                }
            }
        });

    const deleteWellheadRow = (): void => {
        if (count > 0) {
            dispatch(
                PumpScheduleEditActions.setFieldError(
                    'proppantLoadsCountError',
                    EditActions.WELLHEAD_PROPPANT_LOFA_COUNT_ERROR_MESSAGE,
                    wellhead.id,
                    null,
                ),
            );
        } else if (wellhead) {
            if (proppantLoadsCountError === '' && !isNewRow) {
                getLoadsOfJobWellname({
                    variables: {
                        input: {
                            jobId: jobId,
                            wellhead: wellhead.wellName,
                        },
                    },
                });
            } else if (isNewRow) {
                dispatch(PumpScheduleEditActions.deleteWellhead(wellhead));
            }
        }
    };

    const handleWellnameUpdate = (value: string): void => {
        const currWellnames = state.pumpScheduleEdit.currentValues
            .filter((w: any) => {
                return w.isDeleted !== undefined && w.isDeleted == true ? false : true;
            })
            .map(w => w.wellName);
        const isDuplicate = currWellnames.filter(it => it === value);
        const isTooBig = value.length > 50;
        if (isDuplicate && isDuplicate.length > 0) {
            dispatch(
                PumpScheduleEditActions.setFieldError(
                    'wellheadError',
                    EditActions.WELLHEAD_DUPLICATE_ERROR_MESSAGE,
                    wellhead.id,
                    null,
                ),
            );
        } else {
            if (isTooBig) {
                dispatch(
                    PumpScheduleEditActions.setFieldError(
                        'wellheadError',
                        'Wellname must be less than 50 characters',
                        wellhead.id,
                        null,
                    ),
                );
            } else {
                dispatch(PumpScheduleEditActions.setFieldError('wellheadError', '', wellhead.id, null));
            }
        }

        dispatch(PumpScheduleEditActions.updateWellhead(wellhead, InputTitleEnum.WELL_NAME, value));
    };

    const handleChangeWellhead = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
        fieldName: string,
    ): void => {
        const value = event.target.value ? event.target.value : '';

        if (value.length > 50) {
            dispatch(
                PumpScheduleEditActions.setFieldError(
                    'wellheadError',
                    'Wellname must be less than 50 characters',
                    wellhead.id,
                    null,
                ),
            );
        } else {
            dispatch(PumpScheduleEditActions.setFieldError('wellheadError', '', wellhead.id, null));
        }

        dispatch(PumpScheduleEditActions.updateWellhead(wellhead, fieldName, event.target.value));
    };

    const handleChangeStages = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
        fieldName: string,
    ): void => {
        const value = event.target.value ? event.target.value : '';

        if (+event.target.value <= 0) {
            dispatch(
                PumpScheduleEditActions.setFieldError(
                    'stagesError',
                    'Stages must be greater than 0',
                    wellhead.id,
                    null,
                ),
            );
        } else {
            dispatch(PumpScheduleEditActions.setFieldError('stagesError', '', wellhead.id, null));
        }

        dispatch(PumpScheduleEditActions.updateWellhead(wellhead, fieldName, +event.target.value));
    };

    return (
        <div className="PumpScheduleEdit-WellContainer">
            {isDeleteEnabled && (
                <i className="material-icons PumpScheduleEdit-DeleteWellheadIcon" onClick={deleteWellheadRow}>
                    highlight_off
                </i>
            )}
            <div className="PumpSchedule-EditWellName">
                <Textfield
                    label="WELL NAME"
                    onChange={(event): void => {
                        handleWellnameUpdate(event.target.value);
                    }}
                    error={!!wellheadError}
                    helperText={wellheadError || ''}
                    value={wellhead.wellName}
                    inputTitle={InputTitleEnum.WELL_NAME}
                />
            </div>
            <div className="PumpSchedule-EditDetail">
                <Textfield
                    label="STAGES"
                    mask={THREE_NUMBERS_MASK}
                    onChange={(event): void => {
                        handleChangeStages(event, InputTitleEnum.STAGES);
                    }}
                    error={!!stagesError}
                    helperText={stagesError || ''}
                    value={wellhead.stages}
                    inputTitle={InputTitleEnum.STAGES}
                />
            </div>
            <div className="PumpSchedule-EditDetail">
                <Textfield
                    label="STAGE DURATION (MIN)"
                    mask={THREE_NUMBERS_MASK}
                    onChange={(event: any) => {
                        dispatch(
                            PumpScheduleEditActions.updateWellhead(
                                wellhead,
                                InputTitleEnum.STAGE_TIME,
                                +event.target.value,
                            ),
                        );
                    }}
                    onBlur={(value: any) => {
                        dispatch(PumpScheduleEditActions.updateWellhead(wellhead, InputTitleEnum.STAGE_TIME, +value));
                    }}
                    value={wellhead.stageTime}
                    inputTitle={InputTitleEnum.STAGE_TIME}
                />
            </div>
            <div className="PumpSchedule-ProppantLoadsError">{proppantLoadsCountError}</div>
        </div>
    );
};

export default WellheadRow;
